import { DatePickerPreset, DateRange } from "@bebit/types";
import {
  startOfMonth,
  endOfMonth,
  eachWeekOfInterval,
  eachDayOfInterval,
  endOfWeek,
  startOfToday,
  addDays,
  subDays,
  startOfDay,
  addYears,
  subYears,
  endOfDay,
  subMonths,
  isMonday,
  previousMonday,
  previousSunday,
} from "date-fns";

/**
 * 有効な期間を返す
 */
export function getDateRangeFromTwoYearsAgoToToday(): DateRange {
  const today = new Date();
  const twoYearsAgo = subYears(today, 2);
  return getDateRange(twoYearsAgo, today);
}

export function getDateRangeFromTwoYearsAgoToTwoDaysAgo(): DateRange {
  const twoDaysAgo = subDays(new Date(), 2);
  const twoYearsAgo = subYears(new Date(), 2);
  return getDateRange(twoYearsAgo, twoDaysAgo);
}

export function getDateRangeFromTwoYearsAgoToTwoYearsFromNow(): DateRange {
  const twoYearsAgo = subYears(new Date(), 2);
  const twoYearsFromNow = addYears(new Date(), 2);
  return getDateRange(twoYearsAgo, twoYearsFromNow);
}

function getDateRange(startDate: Date, endDate: Date): DateRange {
  return {
    start: startOfDay(startDate),
    end: startOfDay(endDate),
  };
}

/**
 * 有効な期間を返す
 */
export function get2YearsPeriodFrom2DaysAgo(): DateRange {
  const today = startOfToday();
  const twoYearsBefore = getDate2YearsAgo(today);
  const twoDaysAgo = get2DaysAgo();

  return {
    start: twoYearsBefore,
    end: twoDaysAgo,
  };
}

function getDate2YearsAgo(date: Date): Date {
  const twoYearsAgo = new Date(date);
  twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);

  return twoYearsAgo;
}

export function get2DaysAgo(): Date {
  const today = startOfToday();
  return subDays(today, 2);
}

export function buildMonthCalendar(date: Date): Date[][] {
  const sundays = eachWeekOfInterval({
    start: startOfMonth(date),
    end: endOfMonth(date),
  });

  // I want to return 6 arrays for any month, so when 5 arrays are returned, I get the first of the next month.
  if (sundays.length === 5) {
    const dayAfterLastDay = addDays(endOfWeek(sundays[sundays.length - 1]), 1);

    sundays.push(dayAfterLastDay);
  }

  return sundays.map((sunday) => eachDayOfInterval({ start: sunday, end: endOfWeek(sunday) }));
}

function getDateRangeOfThisWeek(date: Date): DateRange {
  const monday = isMonday(date) ? date : previousMonday(date);
  const result: DateRange = {
    start: monday,
    end: endOfDay(subDays(date, 2)),
  };

  return result;
}

function getDateRangeOfLastWeek(date: Date, maxDate?: Date): DateRange {
  const sunday = previousSunday(date);
  const monday = previousMonday(sunday);

  return {
    start: monday,
    end: endOfDay(maxDate && sunday >= maxDate ? maxDate : sunday),
  };
}

function getDateRangeOfThisMonth(date: Date): DateRange {
  const result: DateRange = {
    start: startOfMonth(date),
    end: endOfDay(subDays(date, 2)),
  };

  return result;
}

function getDateRangeOfLastMonth(date: Date, maxDate?: Date): DateRange {
  const lastMonth = subMonths(date, 1);
  const firstDayOfLastMonth = startOfMonth(lastMonth);
  const lastDayOfLastMonth = endOfMonth(lastMonth);
  const result: DateRange = {
    start: firstDayOfLastMonth,
    end: endOfDay(maxDate && maxDate <= lastDayOfLastMonth ? maxDate : lastDayOfLastMonth),
  };

  return result;
}

function getDateRangeOfXDaysFromDate(date: Date, days: number): DateRange {
  const result: DateRange = {
    start: startOfDay(subDays(date, days)),
    end: endOfDay(date),
  };

  return result;
}

export function getDateRangeOfPreset(preset: DatePickerPreset, maxDate?: Date) {
  const today = startOfToday();

  switch (preset) {
    case DatePickerPreset.LastMonth:
      return getDateRangeOfLastMonth(today, maxDate);
    case DatePickerPreset.LastWeek:
      return getDateRangeOfLastWeek(today, maxDate);
    case DatePickerPreset.ThisMonth:
      return getDateRangeOfThisMonth(today);
    case DatePickerPreset.ThisWeek:
      return getDateRangeOfThisWeek(today);
    case DatePickerPreset.Last28Days:
      return getDateRangeOfXDaysFromDate(subDays(today, 2), 27);
    case DatePickerPreset.Last7Days:
      return getDateRangeOfXDaysFromDate(subDays(today, 2), 6);
    default:
      return getDateRangeOfThisWeek(today);
  }
}
